import React, { useState, useEffect } from 'react';
import { 
  getFirestore, 
  collection, 
  query, 
  where, // 🔑 Added missing 'where' import
  onSnapshot, 
  doc, 
  updateDoc,
  getDocs,
  increment, 
  serverTimestamp 
} from 'firebase/firestore';
import toast from 'react-hot-toast';
import { 
  FaShieldAlt, 
  FaFlag, 
  FaCheckCircle, 
  FaTrashAlt, 
  FaBan, 
  FaUndo, 
  FaExclamationTriangle // 🔑 Added missing icon import
} from 'react-icons/fa';

export default function CopyrightTab() {
  const db = getFirestore();
  
  const [legalReports, setLegalReports] = useState([]);
  const [communityFlags, setCommunityFlags] = useState([]);
  const [flaggedArtists, setFlaggedArtists] = useState([]); // 🔑 Added missing state declaration
  const [activeFilter, setActiveFilter] = useState('legal'); // 'legal' or 'community'
  const [loading, setLoading] = useState(true);

  // Real-time listener for copyright reports, community flags, and pending strike reviews
  useEffect(() => {
    setLoading(true);

    // 1. Legal Takedown Claims
    const qLegal = query(collection(db, 'copyright_reports'));
    const unsubLegal = onSnapshot(qLegal, (snapshot) => {
      setLegalReports(snapshot.docs.map(d => ({ id: d.id, ...d.data() })));
    });

    // 2. Community Listener Flags
    const qCommunity = query(collection(db, 'community_flags'));
    const unsubCommunity = onSnapshot(qCommunity, (snapshot) => {
      setCommunityFlags(snapshot.docs.map(d => ({ id: d.id, ...d.data() })));
    });

    // 3. Artists Pending 3-Strike Manual Review
    const qArtistsPending = query(
      collection(db, 'artist_accounts'), 
      where('pending_strike_review', '==', true)
    );
    const unsubArtists = onSnapshot(qArtistsPending, (snapshot) => {
      setFlaggedArtists(snapshot.docs.map(d => ({ id: d.id, ...d.data() })));
    });

    setLoading(false);

    return () => {
      unsubLegal();
      unsubCommunity();
      unsubArtists();
    };
  }, [db]);

  // ACTION 1: Manually Delist Track & Strike Artist
  const handleDelistAndStrike = async (report) => {
    if (!window.confirm(`Are you sure you want to delist "${report.trackTitle || report.trackId}" and issue a copyright strike to the artist?`)) return;

    try {
      // 1. Soft-delete the song
      if (report.trackId) {
        await updateDoc(doc(db, 'songs', report.trackId), {
          isDeleted: true,
          in_review: false,
          delistedAt: serverTimestamp(),
          delistReason: 'Manual Admin Delisting via Copyright Review',
        });
      }

      // 2. Issue +1 strike to artist account if artistId is available
      if (report.artistId) {
        await updateDoc(doc(db, 'artist_accounts', report.artistId), {
          copyright_strikes: increment(1),
        });
      }

      // 3. Resolve the report document
      const collectionName = report.fullName ? 'copyright_reports' : 'community_flags';
      await updateDoc(doc(db, collectionName, report.id), {
        status: 'actioned_delisted',
        resolvedAt: serverTimestamp(),
      });

      toast.success('Track delisted and copyright strike recorded.');
    } catch (error) {
      console.error('Error delisting track:', error);
      toast.error('Failed to delist track. Please try again.');
    }
  };

  // ACTION 2: Approve / Restore Track (Dismiss Claim Legally)
  const handleRestoreTrack = async (report) => {
    try {
      if (report.trackId) {
        await updateDoc(doc(db, 'songs', report.trackId), {
          isDeleted: false,
          in_review: false,
          restoredAt: serverTimestamp(),
        });
      }

      const collectionName = report.fullName ? 'copyright_reports' : 'community_flags';
      await updateDoc(doc(db, collectionName, report.id), {
        status: 'dismissed_restored',
        resolvedAt: serverTimestamp(),
      });

      toast.success('Track restored/approved successfully.');
    } catch (error) {
      console.error('Error restoring track:', error);
      toast.error('Failed to restore track.');
    }
  };

  // ACTION 3: Dismiss Fraudulent Claim & Strike Claimant (TOS Section 11D Circuit Breaker)
  const handleDismissAndStrikeClaimant = async (report) => {
    if (!window.confirm(`Trigger TOS Section 11D Circuit-Breaker? This marks the report as fraudulent and flags/strikes the claimant (${report.email}).`)) return;

    try {
      // Mark report as fraudulent
      await updateDoc(doc(db, 'copyright_reports', report.id), {
        status: 'fraudulent_claim_dismissed',
        flaggedClaimantEmail: report.email,
        resolvedAt: serverTimestamp(),
      });

      // Restore track state if soft-deleted
      if (report.trackId) {
        await updateDoc(doc(db, 'songs', report.trackId), {
          isDeleted: false,
          in_review: false,
        });
      }

      toast.success('Fraudulent report dismissed under Section 11D.');
    } catch (error) {
      console.error('Error executing circuit-breaker:', error);
      toast.error('Failed to dismiss claim.');
    }
  };

  // MANUAL ACTION A: Approve Account Ban & Purge Catalog
  const handleApproveBan = async (artist) => {
    if (!window.confirm(`APPROVE BAN: Are you sure you want to suspend artist "${artist.artistName}" and soft-delete ALL their tracks?`)) return;

    try {
      // 1. Mark account as suspended and clear review flag
      await updateDoc(doc(db, 'artist_accounts', artist.id), {
        isSuspended: true,
        pending_strike_review: false,
        suspendedAt: serverTimestamp(),
      });

      // 2. Soft-delete all tracks uploaded by this artist
      const artistTracksSnap = await getDocs(
        query(collection(db, 'songs'), where('artistId', '==', artist.id), where('isDeleted', '==', false))
      );

      const updatePromises = artistTracksSnap.docs.map(trackDoc => 
        updateDoc(doc(db, 'songs', trackDoc.id), {
          isDeleted: true,
          delistReason: 'Artist Account Suspended via Admin Review (3+ Confirmed Strikes)',
          delistedAt: serverTimestamp(),
        })
      );
      await Promise.all(updatePromises);

      toast.success(`Artist "${artist.artistName}" suspended and catalog soft-deleted.`);
    } catch (error) {
      console.error('Error approving ban:', error);
      toast.error('Failed to execute account ban.');
    }
  };

  // MANUAL ACTION B: Dismiss Strike & Clear Flag (Sabotage Prevention)
  const handleDismissStrike = async (artist) => {
    if (!window.confirm(`DISMISS STRIKE: Reduce strike count for "${artist.artistName}" and keep account active?`)) return;

    try {
      await updateDoc(doc(db, 'artist_accounts', artist.id), {
        copyright_strikes: increment(-1),
        pending_strike_review: false,
      });

      toast.success(`Strike removed. "${artist.artistName}" account restored to normal status.`);
    } catch (error) {
      console.error('Error dismissing strike:', error);
      toast.error('Failed to dismiss strike.');
    }
  };

  const activeReports = activeFilter === 'legal' ? legalReports : communityFlags;

  return (
    <div className="p-6">
      {/* 🚨 CRITICAL BANNER: Artists Pending 3-Strike Review */}
      {flaggedArtists.length > 0 && (
        <div className="mb-8 p-5 bg-amber-50 border-2 border-amber-400 rounded-2xl shadow-sm">
          <div className="flex items-center gap-3 mb-3">
            <FaExclamationTriangle className="text-amber-600 text-xl" />
            <h3 className="text-lg font-black text-amber-900">
              Action Required: {flaggedArtists.length} Artist(s) Flagged for 3-Strike Review
            </h3>
          </div>
          <p className="text-xs text-amber-800 mb-4 font-medium">
            To prevent automated sabotage, these accounts have reached 3 strikes but have NOT been auto-banned. Please review their claims.
          </p>

          <div className="space-y-3">
            {flaggedArtists.map((artist) => (
              <div key={artist.id} className="p-4 bg-white border border-amber-200 rounded-xl flex items-center justify-between gap-4">
                <div>
                  <p className="font-bold text-slate-900">{artist.artistName || 'Unknown Artist'}</p>
                  <p className="text-xs text-slate-500">Email: {artist.email || 'N/A'} | Current Strikes: <span className="font-bold text-red-600">{artist.copyright_strikes}</span></p>
                </div>

                <div className="flex gap-2">
                  <button
                    onClick={() => handleDismissStrike(artist)}
                    className="px-3 py-2 bg-emerald-100 text-emerald-800 hover:bg-emerald-600 hover:text-white rounded-lg text-xs font-bold transition-all"
                  >
                    Dismiss Strike & Keep Live
                  </button>
                  <button
                    onClick={() => handleApproveBan(artist)}
                    className="px-3 py-2 bg-red-600 text-white hover:bg-red-700 rounded-lg text-xs font-bold transition-all"
                  >
                    Approve Account Ban
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Top Controls & Navigation Filter */}
      <div className="flex justify-between items-center pb-6 border-b mb-6">
        <div>
          <h2 className="text-2xl font-black text-slate-900 tracking-tight">Copyright & Verification Queue</h2>
          <p className="text-sm text-slate-500 font-medium">Review legal takedown notices and community sample flags</p>
        </div>

        {/* Filter Toggle Buttons */}
        <div className="flex bg-gray-100 p-1 rounded-xl">
          <button
            onClick={() => setActiveFilter('legal')}
            className={`px-4 py-2 text-xs font-bold rounded-lg transition-all flex items-center gap-2 ${
              activeFilter === 'legal' ? 'bg-white text-blue-600 shadow-sm' : 'text-gray-500 hover:text-slate-800'
            }`}
          >
            <FaShieldAlt /> Legal Takedowns ({legalReports.filter(r => r.status === 'pending' || r.status === 'manual_review_required').length})
          </button>
          <button
            onClick={() => setActiveFilter('community')}
            className={`px-4 py-2 text-xs font-bold rounded-lg transition-all flex items-center gap-2 ${
              activeFilter === 'community' ? 'bg-white text-blue-600 shadow-sm' : 'text-gray-500 hover:text-slate-800'
            }`}
          >
            <FaFlag /> Community Flags ({communityFlags.filter(f => f.status === 'pending_review' || f.status === 'pending').length})
          </button>
        </div>
      </div>

      {/* Reports Table */}
      {loading ? (
        <p className="text-center py-12 text-gray-400">Loading verification queue...</p>
      ) : activeReports.length === 0 ? (
        <div className="text-center py-16 bg-gray-50 rounded-2xl border border-dashed border-gray-200">
          <FaCheckCircle className="mx-auto text-3xl text-emerald-500 mb-2" />
          <p className="font-bold text-slate-700">All clear! No reports in this queue.</p>
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-left text-sm text-slate-600">
            <thead className="bg-slate-50 text-xs uppercase font-bold text-slate-400 tracking-wider">
              <tr>
                <th className="p-4">Target Track</th>
                <th className="p-4">Reporter / Rights Holder</th>
                <th className="p-4">Details / Proof</th>
                <th className="p-4">Status</th>
                <th className="p-4 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {activeReports.map((item) => (
                <tr key={item.id} className="hover:bg-slate-50/50 transition-colors">
                  {/* Track Info */}
                  <td className="p-4 font-semibold text-slate-900">
                    <p className="font-bold">{item.trackTitle || 'Untitled Track'}</p>
                    <p className="text-xs text-slate-400 font-mono">ID: {item.trackId || 'N/A'}</p>
                    {item.isCharting && (
                      <span className="inline-block mt-1 px-2 py-0.5 bg-amber-100 text-amber-800 text-[10px] font-black rounded-full">
                        CHARTING TRACK
                      </span>
                    )}
                  </td>

                  {/* Reporter Info */}
                  <td className="p-4">
                    <p className="font-bold">{item.fullName || 'Anonymous Listener'}</p>
                    <p className="text-xs text-slate-400">{item.email || 'N/A'}</p>
                    {item.company && <p className="text-xs text-slate-400">{item.company}</p>}
                  </td>

                  {/* Details / Proof */}
                  <td className="p-4 max-w-xs">
                    <p className="text-xs text-slate-600 line-clamp-3">
                      {item.proofDescription || item.details || 'No description provided.'}
                    </p>
                  </td>

                  {/* Status Badge */}
                  <td className="p-4">
                    <span className={`px-3 py-1 rounded-full text-xs font-bold ${
                      item.status === 'manual_review_required' ? 'bg-amber-100 text-amber-800' :
                      item.status === 'actioned_delisted' ? 'bg-red-100 text-red-800' :
                      item.status === 'dismissed_restored' ? 'bg-emerald-100 text-emerald-800' :
                      'bg-gray-100 text-gray-700'
                    }`}>
                      {item.status || 'pending'}
                    </span>
                  </td>

                  {/* Action Buttons */}
                  <td className="p-4 text-right space-x-2 whitespace-nowrap">
                    {/* Action 1: Delist */}
                    <button
                      onClick={() => handleDelistAndStrike(item)}
                      className="p-2 bg-red-50 text-red-600 hover:bg-red-600 hover:text-white rounded-lg transition-colors"
                      title="Delist Track & Issue Strike"
                    >
                      <FaTrashAlt />
                    </button>

                    {/* Action 2: Restore */}
                    <button
                      onClick={() => handleRestoreTrack(item)}
                      className="p-2 bg-emerald-50 text-emerald-600 hover:bg-emerald-600 hover:text-white rounded-lg transition-colors"
                      title="Dismiss & Keep Track Live"
                    >
                      <FaUndo />
                    </button>

                    {/* Action 3: TOS Section 11D Circuit-Breaker (Legal Form Only) */}
                    {activeFilter === 'legal' && (
                      <button
                        onClick={() => handleDismissAndStrikeClaimant(item)}
                        className="p-2 bg-purple-50 text-purple-600 hover:bg-purple-600 hover:text-white rounded-lg transition-colors"
                        title="Dismiss Fraudulent Report & Strike Claimant (Section 11D)"
                      >
                        <FaBan />
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}